import { Sel, STagNew } from '#~/stag/index.js';
import { Root } from '@studio/design-system/button';
import { Movement } from '@studio/models/ptz';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { ZoomProps } from './types.js';

// Stable string IDs for the zoom stag uniqueID.
// Per playwright-stag-selectors-only (ai.knowledge/platform): tests
// must not couple to numeric `Movement` enum ordering. Inserting a
// new Movement before ZoomIn would shift `data-movement` numbers; the
// stag stays stable because it is keyed on a string. `data-movement`
// is preserved because choreographer.tsx:27 reads it via
// getTargetIntent as the production contract.
const zoomStagId = (movement: Movement): string =>
  movement === Movement.ZoomIn ? 'zoom-in' : 'zoom-out';

/** `<Zoom>` button component. */
export const Zoom = ({ className, movement, ...props }: ZoomProps): ReactElement => (
  // STag below satisfies `playwright-stag-selectors-only`. `data-movement`
  // emits the numeric Movement enum value (ZoomIn=5, ZoomOut=6); plain
  // `data-movement` is not a viable Playwright selector.
  <Root
    className={twMerge('group absolute h-1/6 w-1/3', className)}
    data-movement={movement}
    tabIndex={-1}
    {...STagNew(Sel.MainRoomRightTrayControls, 'dpad-zoom', zoomStagId(movement))}
    {...props}
    {...STagNew(
      Sel.MainRoomRightTrayControls,
      movement === Movement.ZoomIn ? 'dpad-zoom-in' : 'dpad-zoom-out'
    )}
  >
    <svg viewBox='0 0 76 38'>
      <path
        className='fill-base-100 transition-colors group-hover:fill-secondary group-active:fill-primary'
        d='M75.7027 31.7975C75.8961 32.969 74.9659 34 73.7786 34H2.22141C1.03407 34 0.103922 32.969 0.297307 31.7975C3.2747 13.761 19.0215 0 38 0C56.9785 0 72.7253 13.761 75.7027 31.7975Z'
      />

      <path
        className='group-stroke:stroke-foreground stroke-muted transition-colors group-active:stroke-foreground'
        d={
          movement === Movement.ZoomIn
            ? 'M44.5 18.6L32.5 18.6M38.5 12.6C38.5 17.2863 38.5 19.9137 38.5 24.6'
            : 'M43.8333 17.748L33.1666 17.748'
        }
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='1.5'
      />
    </svg>
  </Root>
);
